import React, { lazy, useState } from 'react';
import { Router, Route, browserHistory, IndexRedirect } from 'react-router';
import { connect } from 'react-redux';
import App from 'chameleon-src/components/App';
import Container from 'chameleon/ui-stack/ui/page/container';
import ContainerWrapper from 'chameleon-src/components/ContainerWrapper';
import HomeContainer from 'chameleon-src/components/HomeContainer';
import requireAuth from 'legacy-src/components/auth/requireAuth';
import editQuestion from 'chameleon-src/routes/editQuestion';
import AuthorItem from 'legacy-src/components/questionBank/editor/AuthorItem';
import wizardPages from 'legacy-packages/wizards/src/routes';
import DynamicAssemessmentsPage from 'chameleon-src/components/DynamicAssemessmentsPage';
import ValidatedRoute from 'chameleon-src/utility/validatedRoute';
import SplitIOHandler from 'chameleon/ui-stack/utilities/split-io-handler';
import assignments from './Assignments';
import dashboardRoutes from './dashboard';
import scoreRubrics from './ScoreByRubrics';
import apReader from './ApReader';
import activities from './Activities';
import trendReport from './TrendReporting';
import results from './results';
import student from './student';
import qbRoutes from './questionBank';
import usage from './usage';
import Maintenance from './Maintenance';

const { wizardPageRoutes, wizardModalRoutes } = wizardPages;

// Secure Browser Components (Lockdown Browser)
const SecureBrowserDownload = lazy(
  () => import('legacy-src/components/SecureBrowserDownload'),
);
const SecureBrowserCheck = lazy(
  () =>
    import('legacy-src/components/SecureBrowserDownload/SecureBrowserCheck'),
);
const SecureBrowserClose = lazy(
  () =>
    import('legacy-src/components/SecureBrowserDownload/SecureBrowserClose'),
);
const ReadinessCheck = lazy(
  () => import('legacy-src/components/apstudent/handlers/ReadinessCheck'),
);

const SubjectsContainer = lazy(
  () => import('legacy-src/components/homepage/subjects/SubjectsContainer'),
);

const Login = lazy(() => import('legacy-src/components/login/Login'));
const PageNotFound = lazy(() => import('legacy-src/components/PageNotFound'));
const Logout = lazy(() => import('legacy-src/components/login/Logout'));
const AdminLanding = lazy(
  () => import('legacy-src/components/homepage/AdminLanding'),
);
const VideoViewerHandler = lazy(
  () => import('legacy-src/utility/VideoViewerHandler'),
);

const FirewallCheck = lazy(
  () => import('legacy-src/utility/FirewallCheck/FirewallCheck'),
);

const AuthRedirect = lazy(
  () => import('legacy-src/components/login/AuthRedirect'),
);

const DashboardRedirect = lazy(
  () => import('legacy-src/components/dashboard/DashboardRedirect'),
);

const TeacherSppPreviewPlayer = lazy(
  () => import('chameleon/ui-stack/ui/spp/player/teacher'),
);

const SharedAssessmentPage = lazy(
  () => import('chameleon-src/components/SharedAssessmentPage'),
);

const SharedAssessmentLinkHandler = lazy(
  () =>
    import(
      'chameleon-src/components/SharedAssessmentPage/SharedAssessmentLinkHandler'
    ),
);

const SPPReviewPlayer = lazy(
  () => import('chameleon/ui-stack/ui/spp/player/review'),
);

const WorldLanguageApReaderPlayer = lazy(
  () => import('chameleon/ui-stack/ui/world_language_pilot/player'),
);

const ErrorMessage = lazy(() => import('chameleon-src/components/error'));

export const SPP_PREVIEW_PATH_NAME = 'teacher-spp-preview-player';
export const SPP_REVIEW_PATH_NAME = 'teacher-spp-review-player';

/**
 * @todo - missing routes
 * - 404 pages
 * - logout
 * - ...TBA
 * */

const Routes = ({ showMaintenancePage }) => {
  const [canBypassMaintenance, setCanBypassMaintenance] = useState(null);
  const onRouteChange = ({ routes, location: { pathname, search } }) => {
    // persist current path for routes with left nav. This will be used to take the user
    // back to the page they were at when they're on a page with no left Nav ie Results Pages

    //in case the last route is component function
    const currentRoute = routes[routes.length - 1].path
      ? routes[routes.length - 1]
      : routes[routes.length - 2];

    const { leftNav = true } = currentRoute;
    let basePath = pathname;
    if (search) {
      basePath += search;
    }
    if (leftNav) {
      localStorage.setItem('PREV_ROUTE', `${basePath}`);
    }
  };

  const isMaintenancePageRequired =
    showMaintenancePage !== 'FAIL' ? showMaintenancePage : false;

  if (isMaintenancePageRequired) {
    let canBypass = false;
    const maintenanceSchool = localStorage.getItem('maintenance-school');
    if (maintenanceSchool) {
      if (canBypassMaintenance === null) {
        const flagHandler = new SplitIOHandler({
          key: maintenanceSchool,
          splitName: 'show_maintenance_page',
          callback: (flag) => {
            setCanBypassMaintenance(flag === 'off');
          },
        });
        flagHandler.invoke();
      }
    }
    if (canBypassMaintenance) {
      canBypass = true;
    }
    if (!canBypass) {
      return <Maintenance />;
    }
  }

  return (
    <Router history={browserHistory}>
      <Route path="auth/redirect" component={AuthRedirect} />
      <Route path="readinesscheck" component={FirewallCheck} />
      <Route path="/" component={requireAuth(App)}>
        {wizardPageRoutes}
        <Route path="error" component={ErrorMessage}>
          <Route path=":id" />
        </Route>
        <Route path="login" component={Login} />
        <Route path="logout" component={Logout} />

        {/* Secure Browser Routes */}
        <Route path="lockdown" component={SecureBrowserDownload} />
        <Route path="securebrowsercheck" component={SecureBrowserCheck} />
        <Route path="close" component={SecureBrowserClose} />

        <Route path=":subjectId/readinesscheck" component={ReadinessCheck} />
        <Route path="subjects/preview" component={SubjectsContainer} />
        <Route path="admin" component={AdminLanding} />
        <Route path="coordinator" component={AdminLanding} />
        <Route
          name="videoViewer"
          path="d/:videoId"
          component={VideoViewerHandler}
        />
        <Route
          name="dashboardRedirect"
          path=":subject/dashboard_redirect"
          component={ValidatedRoute(DashboardRedirect)}
        />
        <Route
          path=":subject"
          component={ContainerWrapper(ValidatedRoute(Container))}
          onChange={onRouteChange}
        >
          <IndexRedirect to="home" />
          <Route
            name="home"
            path="home(/preview/item/:previewId)"
            component={ValidatedRoute(HomeContainer)}
          />
          <Route
            name="sharedAssessment"
            path="shared-quizzes"
            component={SharedAssessmentPage}
          />
          <Route
            name="acceptSharelinkHandler"
            path="shared-quizzes/:shareKey"
            component={SharedAssessmentLinkHandler}
          />
          {assignments}
          {results}
          {activities}
          {trendReport}
          {dashboardRoutes}
          {qbRoutes}
          {usage}
          <Route
            name="createNewItem"
            path="author/question/:qtype"
            component={AuthorItem}
          />
          <Route
            name="dynamicAssessmentPage"
            path="dynamic-assessments"
            component={DynamicAssemessmentsPage}
          />
        </Route>
        {scoreRubrics}
        {apReader}
        {editQuestion}
        {student}
        <Route path=":subject">
          <Route
            name={SPP_PREVIEW_PATH_NAME}
            path="assessments/spp/preview/:assessmentId"
            component={ValidatedRoute(TeacherSppPreviewPlayer)}
            hideTopBar
            exact
          />
          <Route
            name={SPP_REVIEW_PATH_NAME}
            path="assessments/spp/review/:assignmentId/students/:studentId"
            component={ValidatedRoute(SPPReviewPlayer)}
            hideTopBar
            exact
          />
          <Route
            name="world-language-ap-reader-player-results"
            path="assessments/world-language-ap-reader/results/:activityId/students/:studentId"
            component={ValidatedRoute(WorldLanguageApReaderPlayer)}
            hideTopBar
            exact
          />
        </Route>
        <Route path="*" component={PageNotFound} />
      </Route>
      {wizardModalRoutes}
    </Router>
  );
};

Routes.displayName = 'Routes';

const mapStateToProps = (state) => ({
  showMaintenancePage: state.featureFlag.show_maintenance_page,
});

export default connect(mapStateToProps)(Routes);
